<template>
  <el-drawer
    :visible.sync="innerVisible"
    direction="rtl"
    size="440px"
    :with-header="false"
    custom-class="assistant-chat-drawer"
    @open="onOpen"
    @closed="onClosed"
  >
    <div class="chat-shell">
      <header class="chat-head">
        <div>
          <div class="chat-head-title">
            <i class="el-icon-chat-dot-round"></i>
            <span>AI 巡场助手</span>
          </div>
          <p v-if="healthSnapshot" class="chat-scope">{{ healthSnapshot.scopeLabel || '当前范围' }} · {{ healthSnapshot.today }}</p>
        </div>
        <div class="chat-head-actions">
          <el-button type="text" icon="el-icon-refresh" :loading="loading" title="重新检查" @click="resetChat" />
          <el-button type="text" icon="el-icon-close" @click="innerVisible = false" />
        </div>
      </header>

      <div v-if="healthSnapshot" class="chat-summary">
        <el-tag v-if="healthSnapshot.summary.critical" type="danger" size="mini">紧急 {{ healthSnapshot.summary.critical }}</el-tag>
        <el-tag v-if="healthSnapshot.summary.warning" type="warning" size="mini">待完善 {{ healthSnapshot.summary.warning }}</el-tag>
        <el-tag v-if="healthSnapshot.summary.info" type="info" size="mini">建议 {{ healthSnapshot.summary.info }}</el-tag>
        <span class="chat-summary-text">{{ healthSnapshot.overviewText }}</span>
      </div>

      <p class="chat-hint">{{ hintText }}</p>

      <div ref="scroll" class="chat-body">
        <div
          v-for="(msg, idx) in messages"
          :key="idx"
          class="chat-row"
          :class="'role-' + msg.role"
        >
          <div class="chat-bubble">
            <pre class="chat-text">{{ msg.text }}<span v-if="msg.streaming" class="chat-cursor">|</span></pre>
            <div v-if="msg.cards && msg.cards.length" class="chat-cards">
              <div
                v-for="(card, ci) in msg.cards"
                :key="card.code + ci"
                class="chat-card"
                :class="'sev-' + card.severity"
              >
                <div class="chat-card-head">
                  <el-tag :type="sevTag(card.severity)" size="mini">{{ sevLabel(card.severity) }}</el-tag>
                  <span>{{ card.title }}</span>
                </div>
                <p class="chat-card-msg">{{ card.message }}</p>
                <el-button type="text" size="mini" @click="goAction({ label: card.actionLabel, path: card.actionPath, meta: card.meta })">
                  {{ card.actionLabel }} →
                </el-button>
                <el-button type="text" size="mini" @click="askAbout(card)">问怎么处理</el-button>
              </div>
            </div>
            <div v-if="msg.proposedActions && msg.proposedActions.length" class="chat-proposed">
              <el-button
                v-for="(pa, pi) in msg.proposedActions"
                :key="pi"
                type="warning"
                plain
                size="mini"
                :loading="actionLoading === pa.id"
                @click="runProposed(pa, msg)"
              >
                {{ pa.label }}
              </el-button>
            </div>
            <div v-if="msg.actions && msg.actions.length" class="chat-actions">
              <el-button
                v-for="(act, ai) in msg.actions"
                :key="ai"
                type="primary"
                plain
                size="mini"
                @click="goAction(act)"
              >
                {{ act.label }}
              </el-button>
            </div>
          </div>
        </div>
        <div v-if="loading && !streaming" class="chat-row role-assistant">
          <div class="chat-bubble chat-typing">{{ loadingHint }}</div>
        </div>
      </div>

      <div v-if="suggestions.length" class="chat-suggestions">
        <el-tag
          v-for="(s, si) in suggestions"
          :key="si"
          size="small"
          effect="plain"
          class="chat-chip"
          @click="send(s)"
        >
          {{ s }}
        </el-tag>
      </div>

      <footer class="chat-foot">
        <el-input
          v-model="input"
          type="textarea"
          :rows="2"
          resize="none"
          placeholder="输入问题，例如：缺少巡场计划怎么办？"
          @keydown.native.enter.exact.prevent="send()"
        />
        <el-button type="primary" :loading="loading" :disabled="!input.trim() && !loading" @click="send()">
          发送
        </el-button>
      </footer>
    </div>
  </el-drawer>
</template>

<script>
import { assistantChat, getAssistantConfig, executeAssistantAction } from '@/api/admin'

export default {
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      innerVisible: false,
      messages: [],
      suggestions: [],
      healthSnapshot: null,
      input: '',
      loading: false,
      streaming: false,
      opened: false,
      contextItemCode: null,
      pendingPrefill: null,
      assistantConfig: null,
      actionLoading: null
    }
  },
  computed: {
    hintText() {
      if (!this.assistantConfig) return '正在加载助手配置…'
      if (this.assistantConfig.llmEnabled && this.assistantConfig.mode === 'llm') {
        const m = this.assistantConfig.modelsResolved?.adminChat || this.assistantConfig.model
        const model = m ? ` · ${m}` : ''
        return `大模型模式：规则引擎提供事实，LiteLLM 生成回答${model}`
      }
      if (this.assistantConfig.llmEnabled) {
        return '已配置 LiteLLM，当前降级为规则引擎（请在「系统设置 → AI 配置」检查）'
      }
      return '规则引擎模式：在「系统设置 → AI 配置」填写 LiteLLM Key；话术在「助手话术」维护'
    },
    loadingHint() {
      if (this.assistantConfig?.llmEnabled) return '正在调用大模型分析…'
      return '正在分析当前项目状态…'
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(v) { this.innerVisible = v }
    },
    innerVisible(v) {
      this.$emit('update:visible', v)
    }
  },
  mounted() {
    this.$root.$on('assistant-context-change', this.onContextChange)
  },
  beforeDestroy() {
    this.$root.$off('assistant-context-change', this.onContextChange)
  },
  methods: {
    async loadConfig() {
      try {
        this.assistantConfig = await getAssistantConfig()
      } catch {
        this.assistantConfig = { llmEnabled: false, mode: 'rules' }
      }
    },
    openWithContext(ctx = {}) {
      this.contextItemCode = ctx.itemCode || ctx.item?.code || null
      this.pendingPrefill = ctx.prefill || (ctx.item ? `「${ctx.item.title}」应该怎么处理？` : null)
      this.innerVisible = true
      if (this.innerVisible) this.applyContext()
    },
    onOpen() {
      this.loadConfig()
      if (this.pendingPrefill) {
        this.applyContext()
        return
      }
      if (!this.opened) {
        this.opened = true
        this.bootstrap()
      }
    },
    onClosed() {
      this.pendingPrefill = null
    },
    onContextChange() {
      if (!this.innerVisible) return
      this.healthSnapshot = null
      const last = this.messages.filter(m => m.role === 'user').pop()
      this.requestChat(last ? last.text : '', { silent: !last })
    },
    async applyContext() {
      this.messages = []
      this.suggestions = []
      this.opened = true
      if (this.pendingPrefill) {
        const prefill = this.pendingPrefill
        this.pendingPrefill = null
        await this.send(prefill)
      } else {
        await this.bootstrap()
      }
    },
    async bootstrap() {
      await this.requestChat('')
    },
    async resetChat() {
      this.messages = []
      this.suggestions = []
      this.contextItemCode = null
      this.opened = true
      await this.bootstrap()
    },
    askAbout(card) {
      this.contextItemCode = card.code
      this.send(`「${card.title}」应该怎么处理？`)
    },
    async send(text) {
      const message = (text != null ? text : this.input).trim()
      if (!message || this.loading) return
      this.input = ''
      this.messages.push({ role: 'user', text: message })
      this.scrollBottom()
      await this.requestChat(message)
    },
    buildHistory() {
      return this.messages
        .filter(m => !m.streaming)
        .slice(-10)
        .map(m => ({ role: m.role, text: m.text }))
    },
    async requestChat(message, opts = {}) {
      this.loading = true
      try {
        const res = await assistantChat({
          message,
          horizonDays: 7,
          history: this.buildHistory(),
          contextItemCode: this.contextItemCode
        })
        if (res.healthSnapshot) this.healthSnapshot = res.healthSnapshot
        if (message && opts.silent) {
          this.suggestions = res.suggestions || this.suggestions
          return
        }
        await this.appendReply(res)
      } catch (e) {
        this.messages.push({ role: 'assistant', text: '请求失败：' + (e.message || '未知错误') })
      } finally {
        this.loading = false
        this.scrollBottom()
      }
    },
    async runProposed(pa) {
      try {
        await this.$confirm(pa.confirm || '确认执行此操作？', '助手操作', { type: 'warning' })
      } catch { return }
      this.actionLoading = pa.id
      try {
        const res = await executeAssistantAction({
          action: pa.action,
          payload: pa.payload || {}
        })
        this.messages.push({
          role: 'assistant',
          text: res.message || '操作已完成',
          mode: 'action'
        })
        this.$root.$emit('assistant-panel-refresh')
      } catch (e) {
        this.messages.push({ role: 'assistant', text: '操作失败：' + (e.message || '未知错误') })
      } finally {
        this.actionLoading = null
        this.scrollBottom()
      }
    },
    async appendReply(res) {
      const fullText = res.reply || '暂无回复'
      const useStream = res.mode !== 'llm'
      const msg = {
        role: 'assistant',
        text: useStream ? '' : fullText,
        actions: res.actions || [],
        cards: res.cards || [],
        proposedActions: res.proposedActions || [],
        mode: res.mode,
        streaming: useStream
      }
      this.messages.push(msg)
      this.suggestions = res.suggestions || []
      if (useStream) {
        this.streaming = true
        for (let i = 0; i < fullText.length; i++) {
          msg.text += fullText[i]
          if (i % 2 === 0) {
            await new Promise(r => setTimeout(r, 6))
            this.scrollBottom()
          }
        }
        msg.streaming = false
        this.streaming = false
      }
      this.$root.$emit('assistant-health-changed', res.healthSnapshot)
    },
    sevLabel(s) {
      return { critical: '紧急', warning: '待完善', info: '建议' }[s] || s
    },
    sevTag(s) {
      return { critical: 'danger', warning: 'warning', info: 'info' }[s] || ''
    },
    scrollBottom() {
      this.$nextTick(() => {
        const el = this.$refs.scroll
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    goAction(act) {
      if (!act.path) return
      const q = {}
      const m = act.meta || {}
      if (m.templateGuid) q.templateGuid = m.templateGuid
      if (m.date) q.date = m.date
      if (m.month) q.month = m.date?.slice(0, 7) || m.month
      this.innerVisible = false
      this.$router.push({ path: act.path, query: Object.keys(q).length ? q : undefined })
    }
  }
}
</script>

<style scoped>
.chat-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--mhx-neutral-50);
}
.chat-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 16px 10px;
  background: var(--mhx-surface);
  border-bottom: 1px solid var(--mhx-neutral-100);
}
.chat-head-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: var(--mhx-neutral-900);
}
.chat-head-title i { color: var(--mhx-primary-deep); font-size: 18px; }
.chat-scope { margin: 4px 0 0; font-size: 12px; color: var(--mhx-neutral-500); }
.chat-head-actions { display: flex; gap: 4px; }
.chat-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--mhx-surface);
  border-bottom: 1px solid var(--mhx-neutral-100);
}
.chat-summary-text { font-size: 12px; color: var(--mhx-neutral-600); flex: 1; min-width: 120px; }
.chat-hint {
  margin: 0;
  padding: 8px 16px;
  font-size: 12px;
  color: var(--mhx-neutral-500);
  background: var(--mhx-surface);
  border-bottom: 1px solid var(--mhx-neutral-100);
}
.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}
.chat-row { display: flex; margin-bottom: 12px; }
.chat-row.role-user { justify-content: flex-end; }
.chat-row.role-assistant { justify-content: flex-start; }
.chat-bubble {
  max-width: 92%;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.5;
  background: var(--mhx-surface);
  border: 1px solid var(--mhx-neutral-100);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.chat-row.role-user .chat-bubble {
  background: var(--mhx-primary-50);
  border-color: var(--mhx-primary-100);
}
.chat-text {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: inherit;
}
.chat-cursor { animation: blink 1s step-end infinite; color: var(--mhx-primary); }
@keyframes blink { 50% { opacity: 0; } }
.chat-cards { margin-top: 10px; display: flex; flex-direction: column; gap: 8px; }
.chat-card {
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid var(--mhx-neutral-100);
  background: var(--mhx-neutral-50);
}
.chat-card.sev-critical { border-left: 3px solid var(--mhx-danger-text); }
.chat-card.sev-warning { border-left: 3px solid var(--mhx-warning-text); }
.chat-card.sev-info { border-left: 3px solid var(--mhx-primary); }
.chat-card-head { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 500; margin-bottom: 4px; }
.chat-card-msg { margin: 0 0 6px; font-size: 12px; color: var(--mhx-neutral-600); line-height: 1.45; }
.chat-actions {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.chat-proposed {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}
.chat-typing { color: var(--mhx-neutral-500); font-style: italic; }
.chat-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 16px;
  border-top: 1px solid var(--mhx-neutral-100);
  background: var(--mhx-surface);
}
.chat-chip { cursor: pointer; }
.chat-foot {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding: 12px 16px 16px;
  background: var(--mhx-surface);
  border-top: 1px solid var(--mhx-neutral-100);
}
.chat-foot .el-textarea { flex: 1; }
</style>

<style>
.assistant-chat-drawer .el-drawer__body { padding: 0; height: 100%; }
</style>
